import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Two-step field deletion: `requestDeleteField(field)` stages the field in
 * `pendingDeleteField` (the confirm dialog renders off this), then the
 * dialog calls `confirmDeleteField` to actually run DELETE. On success,
 * the field is filtered out of `job.fields`. If the deleted field was
 * open in the inline editor, that editor is closed via
 * `onDeletedFieldOpenInEditor`.
 */
export default function useFieldDelete({
  jobId,
  setJob,
  editingFieldId,
  onDeletedFieldOpenInEditor,
}) {
  const [pendingDeleteField, setPendingDeleteField] = useState(null);
  const [deletingField, setDeletingField] = useState(false);

  const requestDeleteField = useCallback(
    (field) => {
      if (!field || deletingField) return;
      setPendingDeleteField(field);
    },
    [deletingField]
  );

  const cancelDeleteField = useCallback(() => {
    if (deletingField) return;
    setPendingDeleteField(null);
  }, [deletingField]);

  const confirmDeleteField = useCallback(async () => {
    const field = pendingDeleteField;
    if (!field || deletingField) return;
    setDeletingField(true);
    try {
      const res = await api.delete(
        `/api/v1/rfp/autopdf/jobs/${jobId}/fields/${field.id}/`
      );
      if (![200, 202, 204].includes(res.status)) {
        throw new Error("Delete failed");
      }
      setJob((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          fields: (prev.fields || []).filter((f) => f.id !== field.id),
        };
      });
      toast.success("Field deleted.");
      if (editingFieldId === field.id) {
        onDeletedFieldOpenInEditor?.();
      }
      setPendingDeleteField(null);
    } catch (err) {
      console.error("Field delete failed:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to delete field.";
      toast.error(msg);
    } finally {
      setDeletingField(false);
    }
  }, [
    pendingDeleteField,
    deletingField,
    jobId,
    setJob,
    editingFieldId,
    onDeletedFieldOpenInEditor,
  ]);

  return {
    pendingDeleteField,
    deletingField,
    requestDeleteField,
    confirmDeleteField,
    cancelDeleteField,
  };
}
